/** 本机门覆盖所有模态框；它不参与服务端登录，也不持久化“已解锁”。 */
import { el } from './dom.ts';
import { createLocalGate, GATE_KEY } from './gate.ts';

export function mountLocalGate(o: {
  root: HTMLElement;
  onLock(): void;
  onUnlock(): void;
  onReset(): Promise<void>;
}, createGate: typeof createLocalGate = createLocalGate) {
  const gate = createGate({ onChange: () => render() });
  const door = el('dialog', { class: 'gate-door', attrs: { 'aria-labelledby': 'gate-title' } });
  const title = el('h1', { text: '星海已锁好', attrs: { id: 'gate-title' } });
  const hint = el('p', { text: '验证这台设备，再回到我们的星海。' });
  const error = el('p', { class: 'form-error', attrs: { role: 'alert' } });
  const unlock = el('button', { class: 'btn btn-primary', text: '验证并打开', attrs: { type: 'button' } });
  const reset = el('button', { class: 'btn btn-ghost', text: '无法验证？退出并重新配对', attrs: { type: 'button' } });
  door.append(el('div', { class: 'gate-content' }, el('div', { class: 'gate-star', text: '✦', attrs: { 'aria-hidden': 'true' } }), title, hint, error, unlock, reset));
  document.body.append(door);
  let locked = false, resetting = false, settings: HTMLDialogElement | null = null;
  let renderSettings: (() => void) | null = null;
  const waiters: (() => void)[] = [];
  const closeOthers = () => {
    if (!locked) return;
    for (const other of document.querySelectorAll<HTMLDialogElement>('dialog[open]')) {
      if (other === door) continue;
      // 先走组件的取消流程，释放图片、定时器，并让等待确认的 Promise 得到 false。
      other.dispatchEvent(new Event('cancel', { cancelable: true }));
      if (other.open) other.close();
    }
    if (!door.open) door.showModal();
  };
  function render() {
    const next = gate.state === 'locked' || gate.state === 'invalid';
    if (next !== locked) {
      locked = next;
      document.documentElement.classList.toggle('gate-locked', locked);
      o.root.inert = locked;
      if (locked) {
        delete reset.dataset.confirmed;
        reset.textContent = '无法验证？退出并重新配对';
        error.textContent = '';
        o.onLock(); closeOthers();
      }
      else {
        door.close(); o.onUnlock();
        for (const resolve of waiters.splice(0)) resolve();
      }
    }
    if (locked) closeOthers();
    const invalid = gate.state === 'invalid';
    title.textContent = invalid ? '本机解锁信息无法读取' : '星海已锁好';
    hint.textContent = invalid ? '请退出并清除这台设备的离线资料，再用邀请码重新加入。' : '使用 Face ID、Touch ID 或设备密码，由系统选择验证方式。';
    unlock.hidden = invalid;
    unlock.disabled = gate.busy || resetting;
    unlock.textContent = gate.busy ? '等待设备验证…' : '验证并打开';
    reset.disabled = gate.busy || resetting;
    renderSettings?.();
  }
  door.addEventListener('cancel', (e) => e.preventDefault());
  // 不能让其它组件关闭门，或在异步完成后用新模态框盖住它。
  door.addEventListener('close', () => { if (locked) closeOthers(); });
  new MutationObserver(closeOthers).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
  unlock.addEventListener('click', () => {
    error.textContent = '';
    void gate.unlock().catch((e: unknown) => { error.textContent = e instanceof Error ? e.message : String(e); });
  });
  reset.addEventListener('click', () => {
    if (reset.dataset.confirmed !== 'yes') {
      reset.dataset.confirmed = 'yes';
      reset.textContent = '确认退出并清除本机离线资料';
      error.textContent = '这台设备将退出，需要新的设备邀请码才能重新加入；服务端照片不受影响。';
      return;
    }
    resetting = true; render();
    void o.onReset().then(() => location.reload()).catch((e: unknown) => {
      error.textContent = e instanceof Error ? e.message : String(e);
      resetting = false; render();
    });
  });
  document.addEventListener('visibilitychange', () => { if (document.hidden) gate.lock(); });
  addEventListener('pagehide', () => gate.lock());
  addEventListener('pageshow', () => { gate.refresh(); render(); });
  addEventListener('storage', (e) => { if (e.key === GATE_KEY || e.key === null) gate.refresh(); });
  // 全局的 B、三指和长按监听器不属于 inert 子树，也必须挡在门外。
  for (const name of ['keydown', 'pointerdown', 'pointerup', 'touchstart'] as const) {
    addEventListener(name, (e) => {
      if (locked && (!(e.target instanceof Node) || !door.contains(e.target))) { e.preventDefault(); e.stopImmediatePropagation(); }
      else if (locked && name === 'touchstart' && (e as TouchEvent).touches.length >= 3) e.stopImmediatePropagation();
    }, { capture: true, passive: false });
  }
  render();
  return {
    get locked() { return locked; },
    /** 开始任何线上或离线读取之前等待；启动中途锁门也不会揭开覆盖层。 */
    wait(): Promise<void> { return locked ? new Promise((resolve) => waiters.push(resolve)) : Promise.resolve(); },
    reset() { gate.reset(); },
    openSettings() {
      if (locked || settings) return;
      const dialog = settings = el('dialog', { class: 'dialog gate-settings', attrs: { 'aria-label': '本机解锁门' } });
      const status = el('p', { class: 'field-hint' });
      const err = el('p', { class: 'form-error', attrs: { role: 'alert' } });
      const change = el('button', { class: 'btn btn-primary', attrs: { type: 'button' } });
      const lock = el('button', { class: 'btn', text: '立即锁好', attrs: { type: 'button' } });
      const close = el('button', { class: 'btn btn-ghost', text: '关闭', attrs: { type: 'button' } });
      let supported: boolean | null = null;
      renderSettings = () => {
        const enabled = gate.state !== 'disabled';
        status.textContent = enabled ? '已开启：刷新、离开页面或手动锁定后，需要再次验证。' : gate.enrolling ? '凭证已准备好，再验证一次就能启用。' : supported === false ? '这个浏览器暂不支持设备验证；需要 HTTPS 和可用的设备验证方式。' : '仅这台浏览器生效，默认关闭。';
        change.textContent = enabled ? '验证并关闭解锁门' : gate.enrolling ? '再验证一次并启用' : '设置设备验证';
        change.disabled = gate.busy || supported === null || supported === false;
        lock.hidden = !enabled;
        lock.disabled = gate.busy;
        close.disabled = gate.busy;
      };
      dialog.append(el('h2', { class: 'dialog-title', text: '本机解锁门' }), status,
        el('p', { class: 'field-hint', text: '挡住日常借看。照片和离线资料不会因此加密；清除浏览器资料后需要重新配对。系统可能同步通行密钥，此开关不会同步到其它浏览器。' }), err,
        el('div', { class: 'dialog-actions' }, change, lock, close));
      const dismiss = () => { renderSettings = null; settings = null; dialog.remove(); if (gate.enrolling) gate.lock(); };
      dialog.addEventListener('close', dismiss, { once: true });
      dialog.addEventListener('cancel', (e) => { if (gate.busy) e.preventDefault(); });
      close.addEventListener('click', () => dialog.close());
      change.addEventListener('click', () => {
        err.textContent = '';
        const action = gate.state === 'disabled' ? gate.enable() : gate.disable();
        void action.catch((e: unknown) => { err.textContent = e instanceof Error ? e.message : String(e); });
      });
      lock.addEventListener('click', () => gate.lock());
      document.body.append(dialog); dialog.showModal(); renderSettings();
      void gate.supported().then((value) => { supported = value; renderSettings?.(); }).catch(() => { supported = false; renderSettings?.(); });
    },
  };
}
